import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiLottoAssistantEvent } from '@homeplay/api';
import { resetActivity, selectActivity, selectUnitToken } from '@homeplay/app';
import { LOTTO_CONSTANTS } from '../constants';
import { buildBetTicketFromSelectedOutcome } from '../utils/ticketHelpers';
import { createAssistantMessage, makeA2UIMessage } from '../utils/messageFactory';
import LottoAssistantStateContext from './lottoAssistantStateContext';
import LottoAssistantActionsContext from './lottoAssistantActionsContext';
import LottoAssistantIntentsContext from './lottoAssistantIntentsContext';
import {
  createQuickPickHandler,
  createBrowseLotteriesHandler,
  createMyBetsHandler,
  createBettingOptionsRequestHandler,
  createLottoResultsHandler,
  createDetailedLotteryResultsHandler,
} from '../utils/intentHandlerFactories';
import { useMetadataManager } from '../hooks/useMetadataManager';
import { useSubmissionLock } from '../hooks/useSubmissionLock';
import { useLottoAssistantMessaging } from '../hooks/useLottoAssistantMessaging';

const LottoAssistantProvider = ({ children }) => {
  const dispatch = useDispatch();
  const unitToken = useSelector(selectUnitToken);
  const activity = useSelector(selectActivity);

  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [flyingTicketSource, setFlyingTicketSource] = useState(null);
  const [inputCommand, setInputCommand] = useState(null);

  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  const metadataManager = useMetadataManager();
  const submissionLock = useSubmissionLock();

  const appendMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const removeMessageById = useCallback((id) => {
    setMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  const removeBetTicketById = useCallback((messageId, ticketIndex) => {
    setMessages((prev) => {
      const messageIndex = prev.findIndex((m) => m.id === messageId);
      if (messageIndex === -1) return prev;

      const message = prev[messageIndex];
      const component = message.a2ui?.components?.[0];
      if (component?.type !== 'bet_tickets') return prev;

      const tickets = component.props.bet_tickets || [];
      if (ticketIndex < 0 || ticketIndex >= tickets.length) return prev;

      const updatedTickets = tickets.filter((_, idx) => idx !== ticketIndex);

      if (updatedTickets.length === 0) {
        return prev.filter((_, idx) => idx !== messageIndex);
      }

      const updatedMessages = [...prev];
      updatedMessages[messageIndex] = {
        ...message,
        a2ui: {
          ...message.a2ui,
          components: [
            {
              ...component,
              props: { ...component.props, bet_tickets: updatedTickets },
            },
          ],
        },
      };
      return updatedMessages;
    });
  }, []);

  const trackLottoEvent = useCallback(
    async (type, params) => {
      if (!unitToken) return;

      const event = {
        type,
        source: 'frontend',
        client_event_id: crypto.randomUUID(),
        timestamp: new Date().toISOString(),
        params,
      };

      try {
        const response = await apiLottoAssistantEvent({
          unitToken,
          conversationId: conversationIdRef.current,
          event,
        });
        const nextConversationId = response?.conversation_id;
        if (nextConversationId && nextConversationId !== conversationIdRef.current) {
          setConversationId(nextConversationId);
          dispatch(resetActivity());
        }
      } catch (error) {
        console.error('trackLottoEvent failed', error);
      }
    },
    [unitToken, dispatch]
  );

  const { sendMessageToApi } = useLottoAssistantMessaging({
    unitToken,
    conversationId,
    activity,
    onAppendMessage: appendMessage,
    onConversationIdChange: setConversationId,
    onSetIsSendingMessage: setIsSendingMessage,
    metadataManager,
    submissionLock,
  });

  const sendTextMessage = useCallback(
    (text) => sendMessageToApi({
      apiText: text,
      metadata: metadataManager.collectMetadata(),
      shouldClearMetadata: true,
    }),
    [sendMessageToApi, metadataManager]
  );

  const handleBettingOptionsRequest = useMemo(
    () => createBettingOptionsRequestHandler(
      metadataManager,
      appendMessage,
      trackLottoEvent
    ),
    [metadataManager, appendMessage, trackLottoEvent]
  );

  const handleOutcomeSelect = useCallback(
    (data) => {
      const betTicket = buildBetTicketFromSelectedOutcome(data);

      const assistantMessage = createAssistantMessage(
        '',
        makeA2UIMessage('bet_tickets', { bet_tickets: [betTicket] })
      );

      if (betTicket.balls === null) {
        metadataManager.stageTickets([betTicket]);
      }

      trackLottoEvent('ticket_created', {
        ticket_id: betTicket.ticket_id,
        draw_id: betTicket.draw_id,
        draw_date: betTicket.draw_date
          ? new Date(betTicket.draw_date * 1000).toISOString()
          : null,
        draw_number: betTicket.draw_number,
        lottery_name: betTicket.lottery_name,
        market_category: betTicket.market_category,
        market_title: betTicket.market_title,
        market_type: betTicket.market_type,
        outcome: betTicket.outcome,
        balls: betTicket.balls,
        stake: betTicket.stake * LOTTO_CONSTANTS.CENTS_MULTIPLIER,
        repeat_for_draws: betTicket.repeat_for_draws,
      });

      appendMessage(assistantMessage);
    },
    [metadataManager, appendMessage, trackLottoEvent]
  );

  const handleLotterySelect = useCallback(
    async (data) => {
      if (isSendingMessage || submissionLock.isLocked) return;

      if (data.shouldShowCategories) {
        handleBettingOptionsRequest(data.lotteryId);
        return;
      }

      await sendMessageToApi({
        apiText: `${data.country} - ${data.shortName}`,
        metadata: { selected_lottery_id: data.lotteryId },
        shouldClearMetadata: false,
      });
    },
    [isSendingMessage, submissionLock, handleBettingOptionsRequest, sendMessageToApi]
  );

  const stateValue = useMemo(
    () => ({
      messages,
      conversationId,
      isSendingMessage,
      flyingTicketSource,
      inputCommand,
      setInputCommand,
    }),
    [messages, conversationId, isSendingMessage, flyingTicketSource, inputCommand]
  );

  const actionsValue = useMemo(
    () => ({
      sendTextMessage,
      appendMessage,
      removeBetTicketById,
      removeMessageById,
      trackLottoEvent,
      setFlyingTicketSource,
    }),
    [sendTextMessage, appendMessage, removeBetTicketById, removeMessageById, trackLottoEvent]
  );

  const intentsValue = useMemo(
    () => ({
      handleQuickPick: createQuickPickHandler(appendMessage),
      handleBrowseLotteries: createBrowseLotteriesHandler(appendMessage, trackLottoEvent),
      handleMyBets: createMyBetsHandler(appendMessage),
      handleBettingOptionsRequest,
      handleLottoResults: createLottoResultsHandler(appendMessage, trackLottoEvent),
      handleDetailedLotteryResults: createDetailedLotteryResultsHandler(
        appendMessage,
        trackLottoEvent
      ),
      handleOutcomeSelect,
      handleLotterySelect,
      stageTickets: metadataManager.stageTickets,
      stageLotteryId: metadataManager.stageLotteryId,
      stageLotteryList: metadataManager.stageLotteryList,
    }),
    [
      appendMessage,
      trackLottoEvent,
      handleBettingOptionsRequest,
      handleOutcomeSelect,
      handleLotterySelect,
      metadataManager,
    ]
  );

  return (
    <LottoAssistantStateContext.Provider value={stateValue}>
      <LottoAssistantActionsContext.Provider value={actionsValue}>
        <LottoAssistantIntentsContext.Provider value={intentsValue}>
          {children}
        </LottoAssistantIntentsContext.Provider>
      </LottoAssistantActionsContext.Provider>
    </LottoAssistantStateContext.Provider>
  );
};

export default LottoAssistantProvider;
